import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ListResponse } from 'lib/api';
import { apiClient } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { RuleStatusValue } from '../types';
import type { Rule } from '../types';
import useFilterQueryValues from '../hooks/useFilterQueryValues';
import useEnqueueSnackbar from '../../../hooks/useEnqueueSnackbar';
import { omit } from 'lodash-es';

interface ChangeStatusResponse {
  data: unknown;
}

interface ChangeStatusPayload {
  ruleId: number;
  status: RuleStatusValue;
}

export const changeRuleStatus = async ({ status, ruleId }: ChangeStatusPayload): Promise<ChangeStatusResponse> => {
  const res = await apiClient.put<{ data: ChangeStatusResponse }>(`/rules/${ruleId}/status`, { status });
  return res.data.data;
};

interface UseChangeRuleStatusOptions {
  config?: MutationConfig<typeof changeRuleStatus>;
}

export const useChangeRuleStatus = ({ config }: UseChangeRuleStatusOptions = {}) => {
  const queryClient = useQueryClient();
  const params = useFilterQueryValues();
  const queryKey = queryKeys.byFilterValues('rules', omit(params, 'paginationRelatedParams'));
  const { onShowAlert } = useEnqueueSnackbar({ delay: 5000 });

  return useMutation({
    ...config,
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey });

      const previousRules = queryClient.getQueryData(queryKey);

      await queryClient.setQueryData(queryKey, (old: ListResponse<Rule>) => {
        const updatedIndex = old.data.findIndex((r) => r.id === payload.ruleId);

        const cacheCopy = { ...old };

        cacheCopy.data.splice(updatedIndex, 1, {
          ...old.data[updatedIndex],
          status: payload.status,
        });

        return cacheCopy;
      });

      return { previousRules };
    },
    onSuccess: async (...args) => {
      onShowAlert(`Rule ${args[1].status}`);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.rule(args[1].ruleId),
        }),
        queryClient.invalidateQueries({
          queryKey: ['rules'],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: async (err, payload, context) => {
      await queryClient.setQueryData(queryKey, (context as { previousRules: ListResponse<Rule> }).previousRules);
      onShowAlert('Error');
      console.error(err);
    },
    mutationFn: changeRuleStatus,
  });
};
